import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { AlertCircle, CheckCircle, Loader2, MailCheck, RefreshCcw } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { RivaledFullLogo } from "../components/logos/RivaledFullLogo";
import { Button } from "../components/ui";
import { authClient, useSession } from "../lib/auth-client";
import { getAuthCallbackUrl } from "../lib/authCallbackUrl";

export const Route = createFileRoute("/verify-email")({
  component: VerifyEmailPage,
  validateSearch: (search: Record<string, unknown>) => ({
    callbackURL: typeof search.callbackURL === "string" ? search.callbackURL : "",
    token: typeof search.token === "string" ? search.token : "",
  }),
});

function getSafeCallbackPath(callbackURL: string): string {
  if (!callbackURL) {
    return "/app/settings?tab=security";
  }

  if (callbackURL.startsWith("/")) {
    return callbackURL;
  }

  try {
    const parsed = new URL(callbackURL);
    if (parsed.origin === window.location.origin) {
      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    }
  } catch {
    return "/app/settings?tab=security";
  }

  return "/app/settings?tab=security";
}

function VerifyEmailPage() {
  const navigate = useNavigate();
  const { token, callbackURL } = Route.useSearch();
  const { data: session } = useSession();
  const [status, setStatus] = useState<"idle" | "verifying" | "success" | "error">(
    token ? "verifying" : "idle",
  );
  const [error, setError] = useState<string | null>(null);
  const [isResending, setIsResending] = useState(false);
  const [resent, setResent] = useState(false);

  const callbackPath = useMemo(() => getSafeCallbackPath(callbackURL), [callbackURL]);
  const email = session?.user?.email ?? "";

  // Poll session to detect when email gets verified (e.g. user clicked link in another tab)
  const checkVerified = useCallback(async () => {
    const { data: freshSession } = await authClient.getSession();
    if (freshSession?.user?.emailVerified) {
      void navigate({ to: callbackPath });
    }
  }, [callbackPath, navigate]);

  useEffect(() => {
    // Only poll when waiting for verification (idle state, no token)
    if (token || status === "success") return;
    const interval = setInterval(checkVerified, 3000);
    return () => clearInterval(interval);
  }, [token, status, checkVerified]);

  useEffect(() => {
    if (!token) {
      return;
    }

    let cancelled = false;

    async function verify() {
      const { error: verifyError } = await authClient.verifyEmail({ query: { token } });
      if (cancelled) {
        return;
      }

      if (verifyError) {
        setError(verifyError.message ?? "This verification link is invalid or expired.");
        setStatus("error");
        return;
      }

      setStatus("success");
      // Force a fresh session fetch so useSession() in the app layout
      // sees emailVerified: true and doesn't redirect back here
      await authClient.getSession({ fetchOptions: { throw: false } });
      setTimeout(() => void navigate({ to: callbackPath }), 1500);
    }

    void verify();

    return () => {
      cancelled = true;
    };
  }, [token]);

  async function handleResend() {
    if (!email) {
      setError("Sign in to resend your verification email.");
      return;
    }

    setError(null);
    setIsResending(true);

    const { error: resendError } = await authClient.sendVerificationEmail({
      callbackURL: getAuthCallbackUrl(callbackPath),
      email,
    });

    setIsResending(false);

    if (resendError) {
      setError(resendError.message ?? "Failed to resend verification email.");
      return;
    }

    setResent(true);
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-surface-950">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_0%,rgba(38,217,181,0.06),transparent_60%)]" />
        <div className="absolute inset-0 noise-overlay" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="relative mx-4 w-full max-w-md"
      >
        <div className="mb-10 flex justify-center">
          <Link to="/">
            <RivaledFullLogo className="h-10 opacity-80 transition-opacity hover:opacity-100" />
          </Link>
        </div>

        <div className="relative overflow-hidden rounded-2xl border border-surface-700/50 bg-surface-900/80 backdrop-blur-sm">
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-500/40 to-transparent" />
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(38,217,181,0.04)_0%,_transparent_60%)]" />

          <div className="relative px-8 py-10">
            {status === "verifying" ? (
              <div className="space-y-4 text-center">
                <div className="flex justify-center">
                  <div className="flex size-12 items-center justify-center rounded-full border border-brand-500/20 bg-brand-500/10">
                    <Loader2 className="size-6 animate-spin text-brand-400" />
                  </div>
                </div>
                <h1 className="font-display text-2xl font-bold uppercase tracking-tight text-white">
                  Verifying Email
                </h1>
                <p className="text-sm text-surface-400">
                  Hang tight while we confirm your email address.
                </p>
              </div>
            ) : status === "success" ? (
              <div className="space-y-4 text-center">
                <div className="flex justify-center">
                  <div className="flex size-12 items-center justify-center rounded-full border border-brand-500/20 bg-brand-500/10">
                    <CheckCircle className="size-6 text-brand-400" />
                  </div>
                </div>
                <h1 className="font-display text-2xl font-bold uppercase tracking-tight text-white">
                  Email Verified
                </h1>
                <p className="text-sm text-surface-400">
                  Your email address is confirmed. You can continue back into Rivaled.
                </p>
                <Button
                  variant="primary"
                  className="mt-2 w-full py-2.5"
                  onClick={() => void navigate({ to: callbackPath })}
                >
                  Continue
                </Button>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="space-y-4 text-center">
                  <div className="flex justify-center">
                    <div className="flex size-12 items-center justify-center rounded-full border border-brand-500/20 bg-brand-500/10">
                      {status === "error" ? (
                        <AlertCircle className="size-6 text-error-400" />
                      ) : (
                        <MailCheck className="size-6 text-brand-400" />
                      )}
                    </div>
                  </div>
                  <h1 className="font-display text-2xl font-bold uppercase tracking-tight text-white">
                    {status === "error" ? "Verification Failed" : "Check Your Inbox"}
                  </h1>
                  <p className="text-sm text-surface-400">
                    {status === "error"
                      ? (error ?? "This verification link is invalid or expired.")
                      : email
                        ? `We sent a verification link to ${email}. Click it to continue.`
                        : "Open the verification link from your inbox, or sign in to resend it."}
                  </p>
                </div>

                {email ? (
                  <div className="space-y-4">
                    <p className="text-center text-xs text-surface-500">
                      This page will redirect automatically once verified.
                    </p>

                    <Button
                      variant="ghost"
                      className="w-full py-2.5"
                      disabled={isResending}
                      leftIcon={
                        isResending ? (
                          <Loader2 className="size-4 animate-spin" />
                        ) : (
                          <RefreshCcw className="size-4" />
                        )
                      }
                      onClick={handleResend}
                    >
                      {isResending ? "Sending..." : "Resend Verification Email"}
                    </Button>

                    {resent && (
                      <p className="text-center text-sm text-brand-400">
                        A fresh verification email is on the way.
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <Link to="/sign-in" className="block">
                      <Button variant="primary" className="w-full py-2.5">
                        Sign In
                      </Button>
                    </Link>
                    <Link to="/sign-up" className="block">
                      <Button variant="ghost" className="w-full py-2.5">
                        Create Account
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}
